import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { useRole } from "@/hooks/use-role";
import { usePlan } from "@/hooks/usePlan";
import { HelpEngine, type KbDoc } from "@/lib/help-assistant/engine";
import { detectIntent, reorderByIntent, type Intent } from "@/lib/help-assistant/intent";
import {
  composeAnswer, composeFlowStep, composeOutcome, matchFlow, TEMPLATES,
  type ComposedResponse,
} from "@/lib/help-assistant/compose";
import { sanitizeQuestion } from "@/lib/help-assistant/sanitize";
import { findFlow } from "@/content/help/flows";
import kb from "@/generated/help-kb.json";

/**
 * Estado da conversa com o assistente de ajuda.
 *
 * Todo o processamento é local e síncrono: o JSON da base vem no bundle (59,9 KB,
 * bem abaixo do teto de 300 KB que exigiria carga sob demanda). Não há chamada de
 * rede para responder — só para registrar o voto de utilidade.
 */

export interface ChatTurn {
  id: string;
  /** Pergunta como a pessoa escreveu. */
  question: string | null;
  response: ComposedResponse;
  intent: Intent;
  /** Voto já dado neste turno, quando houver. */
  vote?: "up" | "down";
}

interface HelpAssistantState {
  turns: ChatTurn[];
  /** Assunto corrente: documento respondido por último. */
  currentTopic: KbDoc | null;
  ask: (pergunta: string) => void;
  openDoc: (docId: string) => void;
  advanceFlow: () => void;
  clearTopic: () => void;
  clearConversation: () => void;
  vote: (turnId: string, voto: "up" | "down") => void;
  suggestionsForScreen: () => ComposedResponse;
  engineSize: number;
}

const HelpAssistantContext = createContext<HelpAssistantState | null>(null);

let contador = 0;
const proximoId = () => `t${++contador}`;

export function HelpAssistantProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { user } = useAuth();
  const { role } = useRole();
  const { plan } = usePlan();

  const engine = useMemo(() => new HelpEngine(kb.docs as KbDoc[]), []);
  const porId = useMemo(
    () => new Map((kb.docs as KbDoc[]).map((d) => [d.id, d])),
    [],
  );

  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [currentTopic, setCurrentTopic] = useState<KbDoc | null>(null);
  const fluxoAtivo = useRef<{ id: string; step: number } | null>(null);

  const contexto = useCallback(
    () => ({
      route: location.pathname,
      role,
      plan,
      currentTopicId: currentTopic?.id ?? null,
    }),
    [location.pathname, role, plan, currentTopic],
  );

  const registrar = useCallback((turn: ChatTurn) => setTurns((t) => [...t, turn]), []);

  const ask = useCallback(
    (pergunta: string) => {
      const intent = detectIntent(pergunta);

      // Fluxo guiado tem precedência: são as perguntas que nenhuma tela sozinha
      // responde, e uma resposta de tela única as responderia pela metade.
      const fluxo = matchFlow(pergunta);
      if (fluxo) {
        fluxoAtivo.current = { id: fluxo.id, step: 0 };
        registrar({
          id: proximoId(),
          question: pergunta,
          response: composeFlowStep(fluxo, 0),
          intent,
        });
        return;
      }

      const ctx = contexto();
      const outcome = engine.search(pergunta, ctx);

      // A intenção reordena apenas o topo, como desempate — não substitui a
      // recuperação, que foi calibrada por medição.
      if (outcome.kind === "answer") {
        const rankeado = reorderByIntent(engine.rank(pergunta, ctx), intent);
        const escolhido = rankeado[0]?.doc ?? outcome.doc;
        setCurrentTopic(escolhido);
        registrar({
          id: proximoId(),
          question: pergunta,
          response: composeAnswer(escolhido, porId, intent),
          intent,
        });
        return;
      }

      registrar({
        id: proximoId(),
        question: pergunta,
        response: composeOutcome(outcome, porId, intent),
        intent,
      });
    },
    [contexto, engine, porId, registrar],
  );

  const openDoc = useCallback(
    (docId: string) => {
      const doc = porId.get(docId);
      if (!doc) return;
      setCurrentTopic(doc);
      registrar({
        id: proximoId(),
        question: null,
        response: composeAnswer(doc, porId, "unknown"),
        intent: "unknown",
      });
    },
    [porId, registrar],
  );

  const advanceFlow = useCallback(() => {
    const ativo = fluxoAtivo.current;
    if (!ativo) return;
    const fluxo = findFlow(ativo.id);
    if (!fluxo) return;

    const proximo = ativo.step + 1;
    if (proximo >= fluxo.steps.length) {
      fluxoAtivo.current = null;
      registrar({
        id: proximoId(),
        question: null,
        response: {
          kind: "start",
          message: TEMPLATES.flowDone,
          suggestions: [],
        },
        intent: "unknown",
      });
      return;
    }
    fluxoAtivo.current = { id: ativo.id, step: proximo };
    registrar({
      id: proximoId(),
      question: null,
      response: composeFlowStep(fluxo, proximo),
      intent: "unknown",
    });
  }, [registrar]);

  const suggestionsForScreen = useCallback(
    (): ComposedResponse =>
      composeOutcome(
        { kind: "start", suggestions: engine.suggestionsFor(contexto()) },
        porId,
        "unknown",
      ),
    [engine, contexto, porId],
  );

  /**
   * O voto de utilidade. O 👎 é o que revela os buracos da base e alimenta o
   * léxico — por isso a pergunta é gravada, já higienizada, junto da tela.
   */
  const vote = useCallback(
    (turnId: string, voto: "up" | "down") => {
      setTurns((atual) => atual.map((t) => (t.id === turnId ? { ...t, vote: voto } : t)));
      const turn = turns.find((t) => t.id === turnId);
      if (!turn || !user?.id) return;

      const docId =
        turn.response.kind === "answer" ? turn.response.docId
        : turn.response.kind === "flow" ? turn.response.docId ?? null
        : null;

      void supabase
        .from("help_feedback")
        .insert({
          user_id: user.id,
          question: sanitizeQuestion(turn.question ?? turn.response.kind),
          doc_id: docId,
          route: location.pathname,
          helpful: voto === "up",
          app_version: import.meta.env.VITE_APP_VERSION ?? null,
        })
        .then(({ error }) => {
          // Falha aqui não incomoda quem está usando, mas não pode sumir: esta
          // lista é o insumo da próxima rodada de curadoria.
          if (error) Sentry.captureException(new Error(`Falha ao gravar feedback da ajuda: ${error.message}`));
        });
    },
    [turns, user, location.pathname],
  );

  const valor: HelpAssistantState = {
    turns,
    currentTopic,
    ask,
    openDoc,
    advanceFlow,
    clearTopic: () => setCurrentTopic(null),
    clearConversation: () => {
      setTurns([]);
      setCurrentTopic(null);
      fluxoAtivo.current = null;
    },
    vote,
    suggestionsForScreen,
    engineSize: engine.size,
  };

  return <HelpAssistantContext.Provider value={valor}>{children}</HelpAssistantContext.Provider>;
}

export function useHelpAssistant(): HelpAssistantState {
  const ctx = useContext(HelpAssistantContext);
  if (!ctx) throw new Error("useHelpAssistant precisa estar dentro de HelpAssistantProvider");
  return ctx;
}
